import React, { useEffect, useState } from 'react';
import {
  Box, Typography, Button, Paper, Select, MenuItem, FormControl, InputLabel,
  CircularProgress, List, ListItem, ListItemText, IconButton, TextField,
  Accordion, AccordionSummary, AccordionDetails, Dialog, DialogTitle,
  DialogContent, DialogActions, Checkbox, ListItemIcon, Avatar, Chip, Tooltip, Menu
} from '@mui/material';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import PersonAddIcon from '@mui/icons-material/PersonAdd';
import SupervisoryCircleIcon from '@mui/icons-material/SupervisorAccount';
import FilterListIcon from '@mui/icons-material/FilterList';
import CloseIcon from '@mui/icons-material/Close';
import GroupIcon from '@mui/icons-material/Group';
import PeopleIcon from '@mui/icons-material/People';
import { useTranslation } from '../i18n/i18n';
import API_ENDPOINTS from '../config/apiConfig';

function getOid(val) {
  if (!val) return '';
  if (typeof val === 'string') return val;
  if (val.$oid) return val.$oid;
  if (val['$id']) return val['$id'];
  return '';
}

export default function Supervisors() {
  const { t } = useTranslation();
  const [loading, setLoading] = useState(false);
  const [supervisors, setSupervisors] = useState([]);
  const [hirees, setHirees] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [anchorEl, setAnchorEl] = useState(null);
  const [menuSupervisorId, setMenuSupervisorId] = useState(null);
  const [addDialogOpen, setAddDialogOpen] = useState(false);
  const [candidatesToPromote, setCandidatesToPromote] = useState([]);
  const [selectedCandidates, setSelectedCandidates] = useState({});

  const fetchData = async () => {
    setLoading(true);
    try {
      const [sres, hres] = await Promise.all([
        fetch(`${API_ENDPOINTS.BASEURL}/api/supervisors`, { credentials: 'include' }),
        fetch(`${API_ENDPOINTS.BASEURL}/api/hirees/details`, { credentials: 'include' }),
      ]);
      const sdata = sres.ok ? await sres.json() : [];
      const hdata = hres.ok ? await hres.json() : [];
      setSupervisors(sdata || []);
      setHirees(hdata || []);
    } catch (err) {
      setSupervisors([]);
      setHirees([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  const assignSupervisorInline = async (hireeEmail, supervisorId) => {
    try {
      const res = await fetch(`${API_ENDPOINTS.BASEURL}/api/hirees/${encodeURIComponent(hireeEmail)}/assign-supervisor`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ supervisor_id: supervisorId || null })
      });
      if (res.ok) fetchData();
    } catch (err) {
      // ignore
    }
  };

  const handleMenuOpen = (event, supId) => {
    setAnchorEl(event.currentTarget);
    setMenuSupervisorId(supId);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
    setMenuSupervisorId(null);
  };

  const handleRevokeSupervisor = async (supId) => {
    handleMenuClose();
    try {
      const sup = supervisors.find(s => s.id === supId);
      if (!sup) return;
      const res = await fetch(`${API_ENDPOINTS.BASEURL}/api/hirees/${encodeURIComponent(sup.email)}/toggle-supervisor`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ is_supervisor: false })
      });
      if (res.ok) fetchData();
    } catch (err) {
      // ignore
    }
  };

  const openAddDialog = () => {
    const candidates = hirees.filter(h => !h.is_supervisor);
    setCandidatesToPromote(candidates);
    setSelectedCandidates({});
    setAddDialogOpen(true);
  };

  const closeAddDialog = () => {
    setAddDialogOpen(false);
  };

  const toggleCandidateSelection = (email) => {
    setSelectedCandidates(prev => ({ ...prev, [email]: !prev[email] }));
  };

  const confirmAddSupervisors = async () => {
    const emails = Object.keys(selectedCandidates).filter(e => selectedCandidates[e]);
    for (const email of emails) {
      try {
        await fetch(`${API_ENDPOINTS.BASEURL}/api/hirees/${encodeURIComponent(email)}/toggle-supervisor`, {
          method: 'POST',
          credentials: 'include',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ is_supervisor: true })
        });
      } catch (err) {
        // ignore individual errors
      }
    }
    setAddDialogOpen(false);
    fetchData();
  };

  const superviseesFor = (supId) => hirees.filter(h => getOid(h.supervisor_id) === supId);

  const filteredSupervisors = supervisors.filter(s => {
    const q = searchQuery.toLowerCase();
    return !q || (s.fullname || '').toLowerCase().includes(q) || (s.email || '').toLowerCase().includes(q);
  });

  const filteredHirees = hirees.filter(h => {
    const q = searchQuery.toLowerCase();
    return !q || (h.fullname || '').toLowerCase().includes(q) || (h.email || '').toLowerCase().includes(q) || (h.position || '').toLowerCase().includes(q);
  });

  return (
    <Box sx={{ px: { xs: 1, sm: 2 }, py: { xs: 1, sm: 2 } }}>
      {/* Search & Add Header */}
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 3, flexDirection: { xs: 'column', sm: 'row' }, gap: 2 }}>
        <TextField
          size="small"
          placeholder={t('search') || 'Search...'}
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          InputProps={{
            startAdornment: <FilterListIcon fontSize="small" sx={{ color: '#62B6CB', mr: 0.5 }} />
          }}
          sx={{ minWidth: { xs: '100%', sm: 200 }, flex: 1, maxWidth: 350 }}
        />
        <Button
          variant="contained"
          size="small"
          startIcon={<PersonAddIcon />}
          onClick={openAddDialog}
          sx={{ textTransform: 'none', fontWeight: 600, boxShadow: 'none', '&:hover': { boxShadow: '0 2px 8px rgba(0,0,0,0.12)' } }}
        >
          {t('add_supervisor')}
        </Button>
      </Box>

      {loading ? (
        <Box sx={{ textAlign: 'center', py: 6 }}>
          <CircularProgress />
          <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
            Loading...
          </Typography>
        </Box>
      ) : (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
          {/* Supervisors Section */}
          <Paper elevation={0} sx={{ p: { xs: 2, sm: 3 }, borderRadius: 2, border: 1, borderColor: 'divider' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 2 }}>
              <Avatar sx={{ width: 36, height: 36, bgcolor: 'primary.main', color: 'white' }}>
                <SupervisoryCircleIcon sx={{ fontSize: 20 }} />
              </Avatar>
              <Typography variant="h6" sx={{ fontWeight: 600 }}>
                {t('all_supervisors')}
              </Typography>
              <Chip label={filteredSupervisors.length} size="small" sx={{ height: 24, fontWeight: 500 }} />
            </Box>

            {filteredSupervisors.length === 0 ? (
              <Box sx={{ textAlign: 'center', py: 4 }}>
                <Typography variant="body2" color="text.secondary">
                  {t('no_supervisors')}
                </Typography>
              </Box>
            ) : (
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                {filteredSupervisors.map(s => (
                  <Accordion
                    key={s.id}
                    disableGutters
                    sx={{
                      borderRadius: 1.5,
                      border: 1,
                      borderColor: 'divider',
                      '&::before': { display: 'none' },
                      boxShadow: 'none'
                    }}
                  >
                    <AccordionSummary
                      expandIcon={<ExpandMoreIcon />}
                      sx={{ minHeight: 48 }}
                    >
                      <Box sx={{ display: 'flex', alignItems: 'center', width: '100%', gap: 1.5 }}>
                        <Avatar sx={{ width: 36, height: 36, bgcolor: 'primary.light', color: 'white', fontSize: '0.85rem', fontWeight: 600 }}>
                          {(s.fullname || s.email || '?').charAt(0).toUpperCase()}
                        </Avatar>
                        <Box sx={{ flex: 1, minWidth: 0 }}>
                          <Typography variant="body1" sx={{ fontWeight: 500 }}>
                            {s.fullname || s.email}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {s.email}
                          </Typography>
                        </Box>
                        <Chip
                          label={superviseesFor(s.id).length}
                          size="small"
                          color="primary"
                          variant="outlined"
                          sx={{ height: 24 }}
                        />
                        <IconButton
                          size="small"
                          onClick={(e) => { e.stopPropagation(); handleMenuOpen(e, s.id); }}
                        >
                          <MoreVertIcon fontSize="small" />
                        </IconButton>
                      </Box>
                    </AccordionSummary>
                    <AccordionDetails sx={{ pt: 0 }}>
                      <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1, fontWeight: 600 }}>
                        {t('supervisees_count', { count: superviseesFor(s.id).length })}
                      </Typography>
                      {superviseesFor(s.id).length === 0 ? (
                        <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic' }}>
                          {t('no_supervisees')}
                        </Typography>
                      ) : (
                        <List dense disablePadding>
                          {superviseesFor(s.id).map(h => (
                            <ListItem key={h.email} sx={{ px: 1, py: 0.5 }}>
                              <Avatar sx={{ width: 28, height: 28, bgcolor: 'grey.200', color: 'grey.600', fontSize: '0.75rem', mr: 1.5 }}>
                                {(h.fullname || h.email || '?').charAt(0).toUpperCase()}
                              </Avatar>
                              <ListItemText
                                primary={<Typography variant="body2" sx={{ fontWeight: 500 }}>{h.fullname || h.email}</Typography>}
                                secondary={<Typography variant="caption" color="text.secondary">{h.email}</Typography>}
                              />
                            </ListItem>
                          ))}
                        </List>
                      )}
                    </AccordionDetails>
                  </Accordion>
                ))}
              </Box>
            )}
          </Paper>

          {/* Hirees Section */}
          <Paper elevation={0} sx={{ p: { xs: 2, sm: 3 }, borderRadius: 2, border: 1, borderColor: 'divider' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 2 }}>
              <Avatar sx={{ width: 36, height: 36, bgcolor: 'primary.main', color: 'white' }}>
                <PeopleIcon sx={{ fontSize: 20 }} />
              </Avatar>
              <Typography variant="h6" sx={{ fontWeight: 600 }}>
                {t('hirees_list')}
              </Typography>
              <Chip label={filteredHirees.length} size="small" sx={{ height: 24, fontWeight: 500 }} />
            </Box>

            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1, px: 1 }}>
              <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600 }}>
                {t('column_hiree')}
              </Typography>
              <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600, width: 180, textAlign: 'right', display: { xs: 'none', sm: 'block' } }}>
                {t('column_supervisor')}
              </Typography>
            </Box>

            {filteredHirees.length === 0 ? (
              <Box sx={{ textAlign: 'center', py: 4 }}>
                <Typography variant="body2" color="text.secondary">
                  {t('no_hirees')}
                </Typography>
              </Box>
            ) : (
              <List disablePadding>
                {filteredHirees.map((h, idx) => (
                  <ListItem
                    key={h.email}
                    sx={{
                      px: 1,
                      py: 1,
                      borderBottom: idx < filteredHirees.length - 1 ? 1 : 0,
                      borderColor: 'divider',
                      '@media (max-width:599.95px)': {
                        flexDirection: 'column',
                        alignItems: 'stretch',
                        '& .MuiListItemSecondaryAction-root': { position: 'static', transform: 'none', mt: 1 },
                        '& .MuiFormControl-root': { minWidth: '100%' }
                      }
                    }}
                    secondaryAction={
                      <FormControl size="small" sx={{ minWidth: 180 }}>
                        <Select
                          value={getOid(h.supervisor_id) || ''}
                          onChange={(e) => assignSupervisorInline(h.email, e.target.value)}
                          displayEmpty
                        >
                          <MenuItem value="">{t('none')}</MenuItem>
                          {supervisors.map(s => (
                            <MenuItem key={s.id} value={s.id}>
                              {s.fullname} {s.email ? `(${s.email})` : ''}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    }
                  >
                    <Avatar sx={{ width: 32, height: 32, bgcolor: 'grey.200', color: 'grey.600', fontSize: '0.8rem', mr: 1.5 }}>
                      {(h.fullname || h.email || '?').charAt(0).toUpperCase()}
                    </Avatar>
                    <ListItemText
                      primary={<Typography variant="body2" sx={{ fontWeight: 500 }}>{h.fullname || h.email}</Typography>}
                      secondary={<Typography variant="caption" color="text.secondary">{h.email}</Typography>}
                    />
                  </ListItem>
                ))}
              </List>
            )}
          </Paper>
        </Box>
      )}

      {/* Context Menu */}
      <Menu anchorEl={anchorEl} open={Boolean(anchorEl)} onClose={handleMenuClose}>
        <MenuItem onClick={() => handleRevokeSupervisor(menuSupervisorId)} sx={{ color: 'error.main' }}>
          {t('revoke_supervisor')}
        </MenuItem>
      </Menu>

      {/* Add Supervisor Dialog */}
      <Dialog open={addDialogOpen} onClose={closeAddDialog} fullWidth maxWidth="sm">
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', pb: 1 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Avatar sx={{ width: 32, height: 32, bgcolor: 'primary.main', color: 'white' }}>
              <PersonAddIcon sx={{ fontSize: 18 }} />
            </Avatar>
            <Typography variant="h6" sx={{ fontWeight: 600 }}>
              {t('add_supervisor')}
            </Typography>
          </Box>
          <IconButton onClick={closeAddDialog} size="small">
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            {t('select_candidates_to_promote')}
          </Typography>
          {candidatesToPromote.length === 0 ? (
            <Box sx={{ textAlign: 'center', py: 4 }}>
              <Typography variant="body2" color="text.secondary">
                {t('no_candidates_available')}
              </Typography>
            </Box>
          ) : (
            <List disablePadding>
              {candidatesToPromote.map(c => (
                <ListItem
                  key={c.email}
                  button
                  onClick={() => toggleCandidateSelection(c.email)}
                  sx={{
                    borderRadius: 1,
                    mb: 0.5,
                    '&:hover': { bgcolor: 'action.hover' }
                  }}
                >
                  <ListItemIcon sx={{ minWidth: 40 }}>
                    <Checkbox edge="start" checked={!!selectedCandidates[c.email]} disableRipple />
                  </ListItemIcon>
                  <Avatar sx={{ width: 32, height: 32, bgcolor: 'grey.200', color: 'grey.600', fontSize: '0.8rem', mr: 1.5 }}>
                    {(c.fullname || c.email || '?').charAt(0).toUpperCase()}
                  </Avatar>
                  <ListItemText
                    primary={<Typography variant="body2" sx={{ fontWeight: 500 }}>{c.fullname || c.email}</Typography>}
                    secondary={<Typography variant="caption" color="text.secondary">{c.email}</Typography>}
                  />
                </ListItem>
              ))}
            </List>
          )}
        </DialogContent>
        <DialogActions sx={{ p: 2.5, gap: 1.5, borderTop: 1, borderColor: 'divider' }}>
          <Button onClick={closeAddDialog} sx={{ textTransform: 'none' }}>
            {t('cancel')}
          </Button>
          <Button
            variant="contained"
            onClick={confirmAddSupervisors}
            disabled={Object.values(selectedCandidates).every(v => !v)}
            sx={{ textTransform: 'none', fontWeight: 600, boxShadow: 'none' }}
          >
            {t('confirm')}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
